import React, { useState, useEffect, ChangeEvent } from 'react';
import { TextField, Button, Box, Typography, Grid } from '@mui/material';
import API from '../../../create/api';
import { useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';

interface Profile {
  name: string;
  contact_number: string;
  contact_number_backup: string;
  nid: string;
  pickup_location: string;
  facebook: string;
}

export default function ProfilePage() {
  const [profile, setProfile] = useState<Profile>({
    name: '',
    contact_number: '',
    contact_number_backup: '',
    nid: '',
    pickup_location: '',
    facebook: '',
  });
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const companyId = useSelector((state: any) => state.auth.user.company.id);

  useEffect(() => {
    async function fetchProfile() {
      setLoading(true);
      try {
        const response = (await API.get(`${process.env.REACT_APP_API_SERVER}/profile`, companyId)) as Profile;

        setProfile(response);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, [companyId]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await API.update(`${process.env.REACT_APP_API_SERVER}/profile`, companyId, profile);
      toastr.success('Success', 'Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toastr.error('Error', 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={4} bgcolor="white" borderRadius={4} boxShadow={1}>
      <Typography variant="h4" gutterBottom>
        User Profile
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Company Name"
            name="name"
            value={profile.name || ''}
            onChange={handleChange}
            variant="outlined"
            InputProps={{ readOnly: !isEditing || loading }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Number"
            name="contact_number"
            value={profile.contact_number || ''}
            onChange={handleChange}
            variant="outlined"
            InputProps={{ readOnly: !isEditing || loading }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Backup Number"
            name="contact_number_backup"
            value={profile.contact_number_backup || ''}
            onChange={handleChange}
            variant="outlined"
            InputProps={{ readOnly: !isEditing || loading }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="NID"
            name="nid"
            value={profile.nid || ''}
            onChange={handleChange}
            variant="outlined"
            InputProps={{ readOnly: !isEditing || loading }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Pickup Location"
            name="pickup_location"
            value={profile.pickup_location || ''}
            onChange={handleChange}
            variant="outlined"
            InputProps={{ readOnly: !isEditing || loading }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Facebook Page"
            name="facebook"
            value={profile.facebook || ''}
            onChange={handleChange}
            variant="outlined"
            InputProps={{ readOnly: !isEditing || loading }}
          />
        </Grid>
      </Grid>
      <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
        {isEditing ? (
          <>
            <Button variant="contained" color="secondary" onClick={() => setIsEditing(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
              Save
            </Button>
          </>
        ) : (
          <Button variant="contained" color="primary" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}
      </Box>
    </Box>
  );
}
